import type { UserDetailResponseDto } from "Infrastructure/Api/Api";
import { useHandleServerError } from "Infrastructure/Utilities/Forms/Hooks/useHandleServerError";
import { useUploadUserAvatarMutation } from "Settings/User/Api/Mutation/uploadUserAvatarMutation";
import {
	getUploadAvatarFormModelSchema,
	type UploadAvatarFormModel,
} from "Settings/User/Components/Form/Schema/userUploadUserAvatarSchema";
import { useAvatarFilePath } from "Settings/User/Hooks/useAvatarFilePath";
import { BlankCard } from "Shared/Components/Cards/BlankCard";
import { FormFileTypes } from "Shared/Components/Form/CustomFormComponent/FormUploadButton";
import { FormUploadGeneral } from "Shared/Components/Form/CustomFormComponent/FormUploadGeneral";
import { zodResolver } from "@hookform/resolvers/zod";
import { Avatar, Box, Grid2, Stack, Typography } from "@mui/material";
import type { FC } from "react";
import { useForm } from "react-hook-form";

const avatarStyle = {
	borderRadius: "50%",
	width: "100px",
	height: "100px",
	border: "4px solid #fff",
	alignSelf: "center",
};

type Props = {
	user: UserDetailResponseDto;
};

export const UserProfileHeader: FC<Props> = ({ user }) => {
	const form = useForm<UploadAvatarFormModel>({
		resolver: zodResolver(getUploadAvatarFormModelSchema()),
		defaultValues: { avatarFile: "" },
	});

	const { control } = form;

	const avatarFilePath = useAvatarFilePath(user.id, user.avatarFileName);

	const { handleServerError } = useHandleServerError(form);
	const { mutate: uploadAvatarFile } = useUploadUserAvatarMutation(user.id);

	const handleSubmitUpload = (avatarFile: File | FileList | string) => {
		if (avatarFile === "") {
			form.reset({ avatarFile: "" });
			return;
		}

		handleServerError(() => {
			uploadAvatarFile(
				{ avatarFile: avatarFile },
				{
					onError: () => {
						form.reset({ avatarFile: "" });
					},
				},
			);
		});
	};

	return (
		<BlankCard sx={{ overflow: "visible", marginTop: "50px" }}>
			<Grid2 container spacing={0} justifyContent="center" alignItems="center">
				<Grid2
					size={{ xs: 12, md: 5, sm: 12, lg: 4 }}
					sx={{
						order: {
							xs: "2",
							sm: "2",
							lg: "1",
						},
					}}
				>
					<Stack
						direction="row"
						textAlign="center"
						justifyContent="flex-start"
						gap={6}
						m={3}
					>
						<Box>
							<Typography color="text.secondary">
								{/* <FileDescriptionIcon width="20" /> */}
							</Typography>
							<Typography variant="h4" fontWeight="600">
								{/* 34 */}
							</Typography>
							<Typography color="textSecondary" variant="h6" fontWeight={400}>
								{/* Orders */}
							</Typography>
						</Box>
					</Stack>
				</Grid2>
				<Grid2
					size={{ xs: 12, sm: 12, lg: 4 }}
					sx={{
						order: {
							xs: "1",
							sm: "1",
							lg: "2",
						},
					}}
				>
					<Box
						display="flex"
						alignItems="center"
						textAlign="center"
						justifyContent="center"
						flexDirection="column"
						sx={{
							mt: "-85px",
						}}
					>
						<Box
							display="flex"
							alignItems="center"
							flexDirection="column"
							position="relative"
						>
							<FormUploadGeneral
								name="avatarFile"
								control={control}
								onChange={handleSubmitUpload}
								accept={FormFileTypes.imageAll}
							>
								<Avatar
									src={avatarFilePath}
									alt="Profile image"
									sx={avatarStyle}
								/>
							</FormUploadGeneral>
						</Box>

						<Box mt={1}>
							<Typography variant="h5">
								{`${user?.name} ${user?.surname}`}
							</Typography>
						</Box>
					</Box>
					{/* </Box> */}
				</Grid2>

				<Grid2
					size={{ xs: 12, sm: 12, lg: 4 }}
					sx={{
						order: {
							xs: "3",
							sm: "3",
							lg: "3",
						},
					}}
				>
					{null}
					{/* Left the Grid here on purpose to keep the layout */}
				</Grid2>
			</Grid2>
		</BlankCard>
	);
};
